/** @format */
import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LocalizedString, StudyPeriod, StudyPeriodLocator } from 'common-typescript/types';
import { LocaleService } from 'sis-common';
import { AppErrorHandler } from 'sis-components';
import { TimelineStudyPeriodsService } from '../../../timeline-study-periods.service';

const MIN_VISIBLE_PERIOD_COUNT: number = 2;

@Component({
    selector: 'app-timeline-multi-period-timing-notification-box',
    standalone: false,
    templateUrl: './timeline-multi-period-timing-notification-box.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineMultiPeriodTimingNotificationBoxComponent {
    readonly plannedPeriods: InputSignal<StudyPeriodLocator[]> = input.required<StudyPeriodLocator[]>();
    readonly currentPeriod: InputSignal<StudyPeriodLocator> = input.required<StudyPeriodLocator>();

    private readonly timelineStudyPeriodsService = inject(TimelineStudyPeriodsService);
    private readonly localeService = inject(LocaleService);
    private readonly translateService = inject(TranslateService);
    private readonly appErrorHandler = inject(AppErrorHandler);

    private readonly allStudyPeriods: Signal<readonly StudyPeriod[]> = toSignal(
        this.timelineStudyPeriodsService.studyPeriods$.pipe(this.appErrorHandler.defaultErrorHandler()),
    );
    private readonly studyPeriodNameMap: Signal<ReadonlyMap<StudyPeriodLocator, LocalizedString>> = computed(
        () =>
            new Map<StudyPeriodLocator, LocalizedString>(
                this.allStudyPeriods()
                    ?.filter(studyPeriod => !!studyPeriod.locator && !!studyPeriod.name)
                    .map(studyPeriod => [studyPeriod.locator, studyPeriod.name]),
            ),
    );

    private readonly sortedPlannedPeriods: Signal<readonly StudyPeriodLocator[]> = computed(
        () => this.plannedPeriods()?.toSorted((a, b) => a.localeCompare(b)) ?? [],
    );

    protected readonly currentPeriodIndexText: Signal<string | null> = computed(() => {
        const studyPeriodNameMap = this.studyPeriodNameMap();
        const sorted = this.sortedPlannedPeriods().filter(
            locator => !!studyPeriodNameMap?.get(locator) && !!this.localeService.localize(studyPeriodNameMap.get(locator)),
        );
        if (sorted.length < MIN_VISIBLE_PERIOD_COUNT) {
            return null;
        }
        const index = sorted.indexOf(this.currentPeriod());
        if (index === -1) {
            return null;
        }
        return `${index + 1}/${sorted.length}`;
    });

    protected readonly timedToPeriods: Signal<string | null> = computed(() => {
        const sorted: readonly StudyPeriodLocator[] = this.sortedPlannedPeriods();
        if (sorted.length < MIN_VISIBLE_PERIOD_COUNT) {
            return null;
        }

        const studyPeriodNameMap: ReadonlyMap<StudyPeriodLocator, LocalizedString> | undefined = this.studyPeriodNameMap();
        if (!studyPeriodNameMap?.size) {
            return null;
        }

        const periodNames: string[] = sorted
            // it's possible that some periods are not found in TimelineStudyPeriodsService.studyPeriods$
            .map(periodLocator => studyPeriodNameMap.get(periodLocator))
            .filter(studyPeriodName => !!studyPeriodName)
            .map(studyPeriodName => this.localeService.localize(studyPeriodName))
            .filter(translatedStudyPeriodName => !!translatedStudyPeriodName);

        // don't show anything unless there are at least two visible names
        if (periodNames.length < MIN_VISIBLE_PERIOD_COUNT) {
            return null;
        }

        // returns e.g. "period name 1, period name 2 and period name 3"
        return `${periodNames.slice(0, -1).join(', ')} ${this.translateService.instant('AND')} ${periodNames[periodNames.length - 1]}.`;
    });
}
